import { ref } from "vue";
import { fetchNotificationSummary } from "../services/api.js";

const unreadCount = ref(0);
const lastNotificationsSeenAt = ref(null);
let notificationPollIntervalId = null;

/**
 * Resolve the polling interval in milliseconds for notification updates.
 *
 * @returns {number} Polling interval in milliseconds.
 */
function resolveNotificationPollIntervalMs() {
  const defaultIntervalSeconds = 60;
  const rawInterval = import.meta.env.VITE_NOTIFICATION_POLL_INTERVAL_SECONDS;
  const parsedInterval = Number(rawInterval);
  const intervalSeconds =
    Number.isFinite(parsedInterval) && parsedInterval > 0
      ? parsedInterval
      : defaultIntervalSeconds;
  return intervalSeconds * 1000;
}

/**
 * Fetch the latest notification summary and update shared state.
 *
 * @returns {Promise<Object>} Notification summary payload.
 */
async function refreshNotificationSummary() {
  try {
    const payload = await fetchNotificationSummary();
    unreadCount.value = payload.unread_count || 0;
    lastNotificationsSeenAt.value = payload.last_notifications_seen_at || null;
    return payload;
  } catch (error) {
    unreadCount.value = 0;
    lastNotificationsSeenAt.value = null;
    return { unread_count: 0, last_notifications_seen_at: null };
  }
}

/**
 * Start polling the notification summary on a fixed interval.
 */
function startNotificationSummaryPolling() {
  if (notificationPollIntervalId !== null) {
    return;
  }
  const intervalMs = resolveNotificationPollIntervalMs();
  notificationPollIntervalId = window.setInterval(refreshNotificationSummary, intervalMs);
}

/**
 * Stop polling the notification summary.
 */
function stopNotificationSummaryPolling() {
  if (notificationPollIntervalId === null) {
    return;
  }
  window.clearInterval(notificationPollIntervalId);
  notificationPollIntervalId = null;
}

/**
 * Shared notification summary helper.
 */
export function useNotificationSummary() {
  return {
    unreadCount,
    lastNotificationsSeenAt,
    refreshNotificationSummary,
    startNotificationSummaryPolling,
    stopNotificationSummaryPolling,
  };
}
